import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { X, RefreshCw } from "lucide-react";
import {
    fetchProgressConsolidationRequest,
    recalculateProgressConsolidationRequest
} from "../../../redux/actions/performanceActions";

export default function PerformanceProgressDetailModal({ progressId, onClose }) {
    const dispatch = useDispatch();
    const consolidationState = useSelector((s) => s.performance?.consolidation || {});
    const data = consolidationState.byProgressId?.[String(progressId)];
    const loading = consolidationState.loadingByProgressId?.[String(progressId)];
    const error = consolidationState.errorByProgressId?.[String(progressId)];

    useEffect(() => {
        if (progressId) {
            dispatch(fetchProgressConsolidationRequest(progressId));
        }
    }, [dispatch, progressId]);

    const handleRecalculate = () => {
        if (progressId) {
            dispatch(recalculateProgressConsolidationRequest(progressId));
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case "NOT_STARTED": return "Non démarré";
            case "IN_PROGRESS": return "En cours";
            case "PENDING_VALIDATION": return "En attente validation";
            case "COMPLETED": return "Terminé";
            default: return status || "Inconnu";
        }
    };

    const formatScore = (val) => {
        if (val === null || val === undefined) return <span className="perf-score-span empty">—</span>;
        return <span className="perf-score-span">{parseFloat(val).toFixed(2)}%</span>;
    };

    if (!progressId) return null;

    return (
        <div className="perf-modal-backdrop" onClick={onClose}>
            <div className="perf-modal" onClick={(e) => e.stopPropagation()}>
                <div className="perf-modal__header">
                    <div>
                        <h2>Détails de l'évaluation</h2>
                        <p>{loading ? "Chargement..." : data ? data.collaborateurNom : ""}</p>
                    </div>
                    <button className="perf-modal__close" onClick={onClose} aria-label="Fermer">
                        <X size={18} />
                    </button>
                </div>

                <div className="perf-modal__body">
                    {loading && (
                        <div className="perf-state perf-state--inline" style={{ textAlign: "center", padding: "40px" }}>
                            <RefreshCw size={24} className="animate-spin" style={{ margin: "0 auto 12px", color: "#204385" }} />
                            Chargement des données de synthèse...
                        </div>
                    )}

                    {error && (
                        <div className="perf-state perf-state--error perf-state--inline">
                            {error}
                        </div>
                    )}

                    {!loading && !error && !data && (
                        <div className="perf-state perf-state--warning perf-state--inline">
                            Aucune donnée trouvée pour cette évaluation.
                        </div>
                    )}

                    {!loading && !error && data && (
                        <>
                            {/* Collaborator header summary info */}
                            <div className="perf-detail-grid">
                                <div className="perf-detail-section">
                                    <h3 className="perf-detail-section-title">Informations Générales</h3>
                                    <div className="perf-detail-row">
                                        <span className="perf-detail-label">Collaborateur :</span>
                                        <span className="perf-detail-value">{data.collaborateurNom}</span>
                                    </div>
                                    <div className="perf-detail-row">
                                        <span className="perf-detail-label">Poste :</span>
                                        <span className="perf-detail-value">{data.jobName || "Non affecté"}</span>
                                    </div>
                                    <div className="perf-detail-row">
                                        <span className="perf-detail-label">Entité :</span>
                                        <span className="perf-detail-value">{data.entityName || "Non affectée"}</span>
                                    </div>
                                    <div className="perf-detail-row">
                                        <span className="perf-detail-label">Manager :</span>
                                        <span className="perf-detail-value">{data.managerNom || "Aucun"}</span>
                                    </div>
                                </div>

                                <div className="perf-detail-section">
                                    <h3 className="perf-detail-section-title">Synthèse de Performance</h3>
                                    <div className="perf-detail-row">
                                        <span className="perf-detail-label">Statut Global :</span>
                                        <span className={`status-badge ${data.globalStatus}`}>
                                            {getStatusText(data.globalStatus)}
                                        </span>
                                    </div>
                                    <div className="perf-detail-row">
                                        <span className="perf-detail-label">Progression :</span>
                                        <span className="perf-detail-value">{parseFloat(data.progressionPourcentage || 0).toFixed(0)}%</span>
                                    </div>
                                    <div className="perf-progressbar" style={{ marginTop: "4px", marginBottom: "8px" }}>
                                        <span style={{ width: `${data.progressionPourcentage || 0}%` }}></span>
                                    </div>
                                    <div className="perf-detail-row">
                                        <span className="perf-detail-label">Score Global :</span>
                                        <span className="perf-detail-value" style={{ color: "#ee645f", fontSize: "14px" }}>
                                            {formatScore(data.scoreGlobal)}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Details section containing steps status */}
                            <div className="perf-detail-grid">
                                <div className="perf-detail-section">
                                    <h3 className="perf-detail-section-title">Détail des Étapes de l'Évaluation</h3>
                                    <div className="perf-modal-steps">
                                        {[
                                            { label: "Objectifs Année N-1", status: data.objectifsNMoins1Status },
                                            { label: "Compétences Générales", status: data.competencesGeneriquesStatus },
                                            { label: "Compétences Spécifiques", status: data.competencesSpecifiquesStatus },
                                            { label: "Plan de Développement", status: data.planDeveloppementStatus },
                                            { label: "Objectifs de l'Année N", status: data.objectifsNStatus }
                                        ].map((step, idx) => (
                                            <div key={idx} className="perf-modal-step-item">
                                                <span className="perf-modal-step-name">{step.label}</span>
                                                <span className={`status-badge ${step.status}`}>
                                                    {getStatusText(step.status)}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>

                <div className="perf-modal__footer">
                    <button className="perf-btn" type="button" onClick={onClose}>
                        Fermer
                    </button>
                    {!loading && data && (
                        <button className="perf-btn perf-btn--primary" type="button" onClick={handleRecalculate}>
                            <RefreshCw size={14} /> Recalculer individuellement
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
